import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { isNil, map, sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import semver from 'semver';

import { Device, useDevice } from '@/api/devices';
import { useExecuteDeviceOta } from '@/api/ota/executeDeviceOta';
import { isOtaPlanActive } from '@/api/ota/helpers';
import { OtaPlan, VersionState } from '@/api/ota/types';
import { useDeviceOtaDetail } from '@/api/ota/useDeviceOtaDetail';
import { useDeviceOtaPreview } from '@/api/ota/useDeviceOtaPreview';
import { useDeviceOtaReadiness } from '@/api/ota/useDeviceOtaReadiness';
import { Null } from '@/components/Null';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { NotOtaEligibleAlert } from '@/components/spotter/statusTab/NotOtaEligibleAlert';
import { OtaProgressBox, OtaSucceededAlert } from '@/components/spotter/statusTab/OtaProgressBox';
import {
    FirmwareUpdateAlertTitle,
    NewerVersionReleaseStatement,
    UsbInstallerAlertButton,
    UsbInstallerLink,
    useTrackUsbInstallerClick,
} from '@/components/spotter/statusTab/firmwareUpdateAlertParts';
import { InlineAlert, InlineAlertBody, InlineAlertPrimaryButton, InlineAlertTitle } from '@/components/ui/InlineAlert';
import { useSpotterContext } from '@/context/SpotterContext';
import { useBmNodeVersionUpdates } from '@/hooks/useBmNodeVersionUpdates';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsSharedDevice } from '@/hooks/useIsSharedDevice';
import { cardBgLightGrey } from '@/theme';
import { contactSupportHref } from '@/utils/urlHelpers';

export const DeviceFirmwareSection = () => {
    const isSharedDevice = useIsSharedDevice();
    const { data: device, isLoading } = useDevice();

    const currentVersion = device?.versionInfo.currentVersion?.version;
    const currentVersionReleasedAt = device?.versionInfo.currentVersion?.releasedAt;

    if (isLoading) {
        return (
            <Stack gap={2} data-testid="device-details-device-firmware-section-loading">
                <Typography variant="h5">Firmware</Typography>
                <GenericSkeleton lines={2} />
            </Stack>
        );
    }

    return (
        <Stack gap={2} data-testid="device-details-device-firmware-section">
            <Typography variant="h5">Firmware</Typography>

            <Typography variant="body2" component="span">
                {'Current firmware version '}
                {currentVersion ? <strong>{currentVersion}</strong> : 'not yet reported'}
                {currentVersionReleasedAt && (
                    <>
                        {' released '}
                        <Timestamp value={currentVersionReleasedAt} unit="date" />
                    </>
                )}
            </Typography>

            {!isSharedDevice && <FirmwareUpdateBox />}
        </Stack>
    );
};

const deviceAtOrPastTarget = (device?: Device, plan?: OtaPlan) => {
    const currentVersion = device?.versionInfo.currentVersion?.version;
    const targetVersion = plan?.targetVersionState?.spotterVersion;

    // Only testing Spotter version here because we never offer BM-only updates
    // and final Spotter version is always last, so a failed plan will always leave
    // Spotter version behind target. If either of those assumptions ever change,
    // we'll need to update this check to include BM node versions.
    return currentVersion && targetVersion && semver.gte(currentVersion, targetVersion);
};

const FirmwareUpdateBox = () => {
    const firmwareOtaFlag = useFeatureFlag('firmwareOta');
    const { data: otaReadiness, isLoading: readinessLoading } = useDeviceOtaReadiness();
    const { data: otaDetail, isLoading: otaLoading } = useDeviceOtaDetail();
    const { data: device, isLoading: deviceLoading } = useDevice();

    if (readinessLoading || otaLoading || deviceLoading) {
        return null;
    }

    if (isOtaPlanActive(otaDetail)) {
        return <OtaProgressBox plan={otaDetail} />;
    }

    if (otaDetail?.status === 'succeeded' && !isNewerVersionAvailable(device)) {
        return <OtaSucceededAlert plan={otaDetail} />;
    }

    if (otaDetail?.status === 'failed' && !deviceAtOrPastTarget(device, otaDetail)) {
        return <OtaFailedAlert plan={otaDetail} />;
    }

    if (otaReadiness?.capability.capable && firmwareOtaFlag) {
        const failedRetryable =
            (otaDetail?.status === 'cleared_failure' || otaDetail?.status === 'failed_safe') &&
            !deviceAtOrPastTarget(device, otaDetail);
        return (
            <>
                {failedRetryable && <OtaFailedRetryableAlert plan={otaDetail} device={device!} />}
                <OtaFirmwareUpdateAlert />
            </>
        );
    }

    return <NotOtaCapableAlert />;
};

const OtaFirmwareUpdateAlert = () => {
    const { data: readiness } = useDeviceOtaReadiness();
    const { data: preview, isLoading: previewLoading } = useDeviceOtaPreview();
    const { data: device } = useDevice();
    const { spotterId } = useSpotterContext();
    const mixpanel = useMixpanel();
    const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);

    const currentVersion = device?.versionInfo.currentVersion?.version;

    // eligibility should be present if readiness is loaded, since a parent is verifying that we're
    // capable, but might as well type guard eligibility at the same time as checking for readiness loaded.
    if (!currentVersion || previewLoading || !readiness || isNil(readiness.eligibility)) {
        return <></>;
    }

    // using latestVersion so we can still get to the ineligible alert even if whatever's blocking
    // eligibility is also causing the preview not to be able to load.
    const latestVersion =
        preview?.targetVersion.spotterVersion ?? device.versionInfo.latestReleaseVersion.version.replace(/^v/, '');

    if (semver.gte(currentVersion, latestVersion)) {
        return <Null data-testid="ota-capable-no-update-available" />;
    }

    if (!readiness.eligibility.eligible) {
        return (
            <NotOtaEligibleAlert
                latestVersion={preview?.targetVersion ?? device.versionInfo.latestReleaseVersion}
                eligibility={readiness.eligibility}
            />
        );
    }

    if (!preview) {
        // incredibly unlikely that we are eligible but do not have a preview (given !previewLoading above)
        // in this case, we're not going to be able to render a useful preview, and if this situation is real
        // we probably shouldn't let them proceed anyway. just fall back to the incapable instructions, but
        // primarily this is a type guard.
        return <NotOtaCapableAlert />;
    }

    return (
        <>
            <InlineAlert
                severity="tip"
                icon={false}
                actionButton={
                    <InlineAlertPrimaryButton
                        onClick={() => {
                            mixpanel?.track('Click OTA update get started', {
                                spotterId,
                                currentVersion,
                                targetVersion: preview.targetVersion.spotterVersion,
                            });
                            setUpdateModalOpen(true);
                        }}
                    >
                        Get started
                    </InlineAlertPrimaryButton>
                }
            >
                <FirmwareUpdateAlertTitle />
                <InlineAlertBody>
                    <NewerVersionReleaseStatement version={preview.targetVersion} /> You can now update your system
                    remotely over-the-air.
                </InlineAlertBody>
            </InlineAlert>
            <OtaFirmwareUpdateDialog
                open={isUpdateModalOpen}
                onClose={() => setUpdateModalOpen(false)}
                currentVersion={preview.currentVersion}
                targetVersion={preview.targetVersion}
            />
        </>
    );
};

export const OtaFirmwareUpdateDialog = ({
    open,
    onClose,
    currentVersion,
    targetVersion,
}: {
    open: boolean;
    onClose: () => void;
    currentVersion: VersionState;
    targetVersion: VersionState;
}) => {
    const { data: device } = useDevice();
    const { spotterId } = useSpotterContext();
    const mixpanel = useMixpanel();
    const { mutate: executeDeviceOta, isPending, error, reset } = useExecuteDeviceOta();

    // Clears an error from a previous attempt, which the dialog would otherwise still show when it
    // reopens: the mutation state outlives the dialog content, which unmounts while `open` is false.
    const dismissDialog = (dismissedFrom: string) => {
        mixpanel?.track('Dismiss OTA update dialog', {
            spotterId,
            dismissedFrom,
            currentVersion: currentVersion.spotterVersion,
            targetVersion: targetVersion.spotterVersion,
            hadError: Boolean(error),
        });
        reset();
        onClose();
    };

    const handleStartUpdate = () => {
        if (!spotterId) {
            return;
        }
        mixpanel?.track('Click OTA update start update', {
            spotterId,
            currentVersion: currentVersion.spotterVersion,
            targetVersion: targetVersion.spotterVersion,
        });
        executeDeviceOta({ spotterId }, { onSuccess: onClose });
    };

    const bmNodeUpdates = useBmNodeVersionUpdates({
        initialVersionState: currentVersion,
        targetVersionState: targetVersion,
    });

    const updates = useMemo(() => {
        const updates: { name: string; from: string; to: string }[] = [];
        if (currentVersion.spotterVersion !== targetVersion.spotterVersion) {
            updates.push({
                name: 'Spotter',
                from: currentVersion.spotterVersion,
                to: targetVersion.spotterVersion,
            });
            const bmUpdates = map(bmNodeUpdates, ({ from, to, name, position }) => {
                return {
                    name,
                    from,
                    to,
                    position,
                };
            });
            updates.push(...sortBy(bmUpdates, 'position'));
        }
        return updates;
    }, [bmNodeUpdates, currentVersion.spotterVersion, targetVersion.spotterVersion]);

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => dismissDialog(reason)}
            aria-labelledby="ota-dialog-title"
            aria-describedby="ota-dialog-description"
        >
            <DialogTitle id="ota-dialog-title" data-testid="ota-dialog-title">
                Update your firmware
                <IconButton
                    aria-label="close"
                    onClick={() => dismissDialog('closeX')}
                    disabled={isPending}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" id="ota-dialog-description" data-testid="ota-dialog-description">
                    This update may take several hours, depending on the strength of your Spotter&apos;s cellular
                    connection. Make sure your Spotter is turned on, has cellular telemetry enabled, and has a clear
                    view of the sky for the entire update. At certain stages of the process, your Spotter will reboot
                    and may temporarily stop data collection.
                </Typography>
                <Typography component="div" variant="body2" sx={{ p: 2, backgroundColor: cardBgLightGrey }}>
                    <Box>
                        <strong>Included updates:</strong>
                    </Box>
                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                        {updates.map(({ name, from, to }) => (
                            <li key={name}>
                                {name} ({from}{' '}
                                <EastIcon
                                    fontSize="inherit"
                                    sx={{ verticalAlign: 'middle', position: 'relative', top: '-1px' }}
                                />{' '}
                                {to})
                            </li>
                        ))}
                    </Box>
                </Typography>
                <InlineAlert severity="error" size="small">
                    <InlineAlertTitle>Once you start the update, it cannot be cancelled.</InlineAlertTitle>
                    <InlineAlertBody>
                        Do not turn off your Spotter, disable telemetry,
                        {device?.isSmartMooring && <> change the sensors attached to your Smart Mooring,</>} or make any
                        other changes to your system during the update.
                    </InlineAlertBody>
                </InlineAlert>
                {error && (
                    <InlineAlert severity="error" size="small" data-testid="ota-dialog-error">
                        <InlineAlertTitle>The update could not be started.</InlineAlertTitle>
                        <InlineAlertBody>{error.message}</InlineAlertBody>
                    </InlineAlert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => dismissDialog('cancel')} variant="text" disabled={isPending}>
                    Cancel
                </Button>
                <Button
                    onClick={handleStartUpdate}
                    autoFocus
                    variant="contained"
                    disabled={isPending || !spotterId}
                    endIcon={isPending ? <CircularProgress size={20} color="inherit" /> : null}
                    data-testid="ota-dialog-confirm-button"
                >
                    Start update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const isNewerVersionAvailable = (device?: Device) => {
    const currentVersion = device?.versionInfo.currentVersion?.version;
    const latestVersion = device?.versionInfo.latestReleaseVersion.version;

    return (
        currentVersion &&
        latestVersion &&
        semver.gt(latestVersion, currentVersion) &&
        semver.gte(latestVersion, '2.0.0')
    ); // never suggest updating a 1.x -- too many ways for it to go wrong
};

const NotOtaCapableAlert = () => {
    const { data: device } = useDevice();
    const trackUsbInstallerClick = useTrackUsbInstallerClick();

    if (!isNewerVersionAvailable(device)) {
        return <Null data-testid="not-ota-capable-no-update-available" />;
    }

    return (
        <InlineAlert
            severity="tip"
            icon={false}
            actionButton={
                <UsbInstallerAlertButton
                    onClick={() => trackUsbInstallerClick({ reason: 'not-capable', control: 'button' })}
                />
            }
        >
            <FirmwareUpdateAlertTitle />
            <InlineAlertBody>
                <NewerVersionReleaseStatement version={device?.versionInfo.latestReleaseVersion} /> Follow the{' '}
                <UsbInstallerLink onClick={() => trackUsbInstallerClick({ reason: 'not-capable', control: 'link' })} />{' '}
                to update your Spotter.
            </InlineAlertBody>
        </InlineAlert>
    );
};

export const OtaFailedAlert = ({ plan }: { plan: OtaPlan }) => {
    const { spotterId } = useSpotterContext();
    const targetSpotterVersion = plan.targetVersionState?.spotterVersion;

    return (
        <InlineAlert
            severity="error"
            actionButton={
                <InlineAlertPrimaryButton
                    href={contactSupportHref({
                        subject: `Spotter firmware update failed: ${spotterId}`,
                        body: `Hi Spotter support, an OTA firmware update for ${spotterId} failed. We cannot retry the update until Support ensures the device is in a safe state and clears the failure.\n\n`,
                    })}
                >
                    Contact Support
                </InlineAlertPrimaryButton>
            }
        >
            <InlineAlertTitle>Firmware update unsuccessful</InlineAlertTitle>
            <InlineAlertBody>
                Your recent over-the-air firmware update
                {targetSpotterVersion ? ` to ${targetSpotterVersion}` : ''} was unsuccessful and your device&apos;s
                performance may be impacted. Please contact Support to troubleshoot.
            </InlineAlertBody>
        </InlineAlert>
    );
};

export const OtaFailedRetryableAlert = ({ plan, device }: { plan: OtaPlan; device: Device }) => {
    const notificationId = `ota-firmware-unsuccessful:${plan.id}`;
    const { spotterId } = device;
    const targetSpotterVersion = plan.targetVersionState?.spotterVersion;

    return (
        <InlineAlert severity="warning" dismissNotificationId={notificationId} notificationContext={{ spotterId }}>
            <InlineAlertTitle>Firmware update unsuccessful</InlineAlertTitle>
            <InlineAlertBody>
                Your recent remote over-the-air firmware update
                {targetSpotterVersion ? ` to ${targetSpotterVersion}` : ''} was unsuccessful. You can try again once
                your Spotter meets the requirements.
            </InlineAlertBody>
        </InlineAlert>
    );
};
